// Plan comptable > Codes tiers > « Codes sans fiche » (T, 03/11/2026) : codes
// 401/411 mouvementés en comptabilité sans fiche client ni fournisseur.
// Aperçu (rattacher à une fiche, créer la fiche avec le nom lu sur les PDF
// Dolibarr, renommer, nom à fournir), puis confirmation par l'administrateur.
// Aucune écriture comptable n'est modifiée : la fiche reprend le code.
import { useCallback, useEffect, useState } from "react";
import api from "../services/api";
import { useAuth } from "../contexts/AuthContext";

const eur = (n) => `${Number(n || 0).toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;
const ACTION = {
  "rattacher": { label: "Rattacher à la fiche", color: "#166534" },
  "créer": { label: "Créer la fiche", color: "#166534" },
  "nom à fournir": { label: "Nom à fournir", color: "#b45309" },
  "renommer": { label: "Renommer le compte de la fiche", color: "#1d4ed8" },
  "à vérifier": { label: "À vérifier", color: "#b91c1c" },
};

export default function OrphanTiersPanel() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [items, setItems] = useState(null);
  const [sel, setSel] = useState({});   // code → true
  const [names, setNames] = useState({}); // code → nom saisi
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const load = useCallback(() => {
    setError("");
    api.tiersCodes.orphans().then(r => { setItems(r.items || []); setSel({}); }).catch(e => setError(e.message));
  }, []);
  useEffect(() => { load(); }, [load]);

  const selectable = (i) => i.action === "rattacher" || i.action === "créer" || (i.action === "nom à fournir" && (names[i.code] || "").trim());
  const chosen = (items || []).filter(i => sel[i.code] && selectable(i));
  const apply = async () => {
    if (!window.confirm(`Créer ou rattacher ${chosen.length} fiche(s) ?\n\nAucune écriture n'est modifiée : chaque fiche reprend le code et les écritures déjà passées sur ce code.`)) return;
    setBusy(true); setError(""); setResult(null);
    try { setResult(await api.tiersCodes.orphansApply(chosen.map(i => ({ code: i.code, name: names[i.code] || undefined })))); load(); }
    catch (e) { setError(e.message); }
    finally { setBusy(false); }
  };

  if (!items) return error ? <div style={{ color: "#b91c1c", fontSize: 13 }}>{error}</div> : null;
  if (!items.length) return null;
  return (
    <div className="card" style={{ marginBottom: 14 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div style={{ fontWeight: 700 }}>Codes sans fiche ({items.length})</div>
        <button className="btn" style={{ fontSize: 12 }} onClick={load}>↻ Actualiser</button>
      </div>
      <div style={{ fontSize: 12.5, color: "#6b7280", margin: "4px 0 8px" }}>
        Codes clients et fournisseurs utilisés en comptabilité (souvent par le relevé, sous le code de l'expert-comptable) sans fiche. Cochez ceux à créer ou à rattacher : aucune écriture n'est modifiée, la fiche reprend le code. Le nom vient des PDF Dolibarr déjà importés ; pour un fournisseur, saisissez-le (ou attendez l'export des tiers).
      </div>
      {error && <div style={{ color: "#b91c1c", fontSize: 13, marginBottom: 6 }}>{error}</div>}
      {items.some(i => i.kind === "client" && i.action === "nom à fournir") && (
        <div style={{ fontSize: 12.5, background: "#fef3c7", color: "#92400e", borderRadius: 8, padding: "6px 10px", marginBottom: 8 }}>
          Des codes clients n'ont pas encore de nom : cliquez d'abord sur <strong>Codes lus sur les PDF…</strong> (plus bas) pour lire les codes imprimés sur vos factures Dolibarr, puis <strong>↻ Actualiser</strong> cet encadré.
        </div>
      )}
      {result && (
        <div style={{ fontSize: 12.5, marginBottom: 8, color: result.skipped?.length ? "#b45309" : "#166534" }}>
          {result.done.length} fiche(s) créée(s) ou rattachée(s).
          {(result.skipped || []).map(s => <div key={s.code}>{s.code} : {s.reason}</div>)}
        </div>
      )}
      <div style={{ maxHeight: 360, overflowY: "auto" }}>
        <table className="table" style={{ width: "100%", fontSize: 12 }}>
          <thead><tr><th></th><th style={{ textAlign: "left" }}>Code</th><th style={{ textAlign: "right" }}>Écritures</th><th style={{ textAlign: "right" }}>Solde</th><th style={{ textAlign: "left" }}>Proposition</th><th style={{ textAlign: "left" }}>Nom</th></tr></thead>
          <tbody>{items.map(i => (
            <tr key={i.code}>
              <td>{isAdmin && <input type="checkbox" disabled={!selectable(i)} checked={!!sel[i.code] && !!selectable(i)} onChange={e => setSel(s => ({ ...s, [i.code]: e.target.checked }))} />}</td>
              <td style={{ fontFamily: "monospace" }}>{i.code}</td>
              <td style={{ textAlign: "right" }}>{i.lines}</td>
              <td style={{ textAlign: "right" }}>{eur(i.balance)}</td>
              <td><span style={{ fontWeight: 600, color: ACTION[i.action]?.color }}>{ACTION[i.action]?.label || i.action}</span>
                {i.source && <div style={{ color: "#6b7280" }}>{i.source}</div>}
                {i.reason && <div style={{ color: "#6b7280" }}>{i.reason}</div>}</td>
              <td>{i.action === "nom à fournir"
                ? (isAdmin ? <input className="input" style={{ width: 200, fontSize: 12, padding: "2px 6px" }} placeholder={i.kind === "client" ? "Nom du client" : "Nom du fournisseur"} value={names[i.code] || ""}
                    onChange={e => { const v = e.target.value; setNames(n => ({ ...n, [i.code]: v })); if (v.trim()) setSel(s => ({ ...s, [i.code]: true })); }} /> : "—")
                : (i.name || "—")}</td>
            </tr>
          ))}</tbody>
        </table>
      </div>
      {isAdmin
        ? <button className="btn primary" style={{ marginTop: 8 }} disabled={busy || !chosen.length} onClick={apply}>✓ Créer ou rattacher {chosen.length} fiche(s)</button>
        : <div style={{ fontSize: 12, color: "#6b7280", marginTop: 6 }}>La confirmation est réservée à l'administrateur.</div>}
    </div>
  );
}
